import { useState, useRef, useEffect } from 'react';

import { DragProps, UseDragReturn } from './types';

/**
 * allows the consumer to call a function duing the mousemove event and a separate function
 * during the mouseup event.
 */
export function useDrag(props: DragProps): UseDragReturn {
  const [isDragging, setIsDragging] = useState(false);
  const lastXY = useRef([NaN, NaN]);

  useEffect(() => {
    if (!isDragging) return;

    function delta(clientX: number, clientY: number) {
      const delta = {
        deltaX: clientX - lastXY.current[0],
        deltaY: clientY - lastXY.current[1],
      };
      lastXY.current = [clientX, clientY];
      return delta;
    }

    function onDrag(e: MouseEvent) {
      props.onDrag(e, delta(e.clientX, e.clientY));
    }

    function onDragEnd(e: MouseEvent) {
      setIsDragging(false);
      props.onDragEnd(e, delta(e.clientX, e.clientY));
    }

    function onDragLeave(_e: MouseEvent) {
      setIsDragging(false);
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', onDragEnd);
      document.removeEventListener('mouseleave', onDragLeave);
    }

    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', onDragEnd);
    document.addEventListener('mouseleave', onDragLeave);

    return () => {
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', onDragEnd);
      document.removeEventListener('mouseleave', onDragLeave);
    };
  });

  return {
    startDrag: (initialPos) => {
      lastXY.current = initialPos;
      setIsDragging(true);
    },
    abortDrag: () => {
      setIsDragging(false);
    },
    isDragging,
  };
}
